import { EntityFactory } from './EntityFactory';
import { ObstacleFactory } from './ObstacleFactory';
import { Level, LaneItemDetails } from './levels/level';
import { LevelUtil } from './levels/LevelUtil';
import { BoostPadAnimated } from './entities/BoostPadAnimated';
import Obstacle, { ObstacleNameValues } from './entities/obstacle/Obstacle';
import { Passenger, PassengerState } from './Passenger';
import TaxiRank from './entities/TaxiRank';
import WaitingPassenger from './WaitingPassenger';
import TaxiDropOff from './entities/TaxiDropOff';
import { TrafficManager } from './TrafficManager';
import { MathExtras } from '../core/utils/MathExtras';
import { ObjectClassificationValues } from '../AIController';
import { ObjectClassification } from './../AIController';
import { Theme } from './themes/theme';
import { LaneEntity } from './entities/LaneEntity';
import { app } from '../core/App';

export interface LaneWeight {
  laneNumber: number;
  distance: number;
  weight: number;
}

export interface ItemConfig {
  className: string;
  maxSpawned: number;
  timeBetweenSpawns: number;
}

export interface ItemTracking {
  className: string;
  currentSpawned: number;
  lastSpawn: number;
}

export interface LaneZone {
  startX: number;
  endX: number;
}

export interface LaneItem {
  classification: ObjectClassificationValues;
  distance: number;
}

// export interface PassengerJourney {
//   passenger: Passenger;
//   taxi: Vehicle;
//   sprite: string;
//   dropPoint: number;
// }

export const BodyTypes = {
  Vehicle: 'vehicle',
  VehicleSensor: 'vehicleSensor',
  TaxiRank: 'taxiRank',
  TaxiDropOff: 'taxiDropOff',
  Boost: 'boost',
  Obstacle: 'obstacle'
} as const;

// const entitiesToTypes = {
//   [ObstacleOilSlick]: typeof ObstacleOilSlick
// }

export class LevelManager {
  // public static itemConfiguration: Map<string, ItemConfig> = new Map([
  //   [BoostPad.name, { className: BoostPad.name, maxSpawned: 5, timeBetweenSpawns: 4000 }],
  //   [ObstacleBananaSkin.name, { className: ObstacleBananaSkin.name, maxSpawned: 2, timeBetweenSpawns: 4000 }],
  //   [ObstacleBoxes.name, { className: ObstacleBoxes.name, maxSpawned: 2, timeBetweenSpawns: 4000 }],
  //   [ObstacleOilSlick.name, { className: ObstacleOilSlick.name, maxSpawned: 2, timeBetweenSpawns: 4000 }],
  //   [ObstacleWetConcrete.name, { className: ObstacleWetConcrete.name, maxSpawned: 2, timeBetweenSpawns: 4000 }]
  // ]);

  protected scene: Phaser.Scene;
  protected currentLevel: Level;
  protected currentTheme: Theme;
  protected obstacleFactory: ObstacleFactory;
  protected entityFactory: EntityFactory;
  protected trafficManager: TrafficManager | undefined;
  protected floorLayer: Phaser.GameObjects.Layer | undefined;
  protected foregroundPavementLayer: Phaser.GameObjects.Layer | undefined;

  protected obstacleList: typeof Obstacle[];
  protected itemConfiguration: Map<string, ItemConfig>;
  protected hasDroppedAllPassengers: boolean;
  protected hasSpawnedAllDropPoints: boolean;

  protected passengers: Passenger[];
  protected taxiRanks: TaxiRank[];
  protected taxiDropOffs: TaxiDropOff[];
  protected itemZones: Map<number, LaneZone[]>;
  protected spawnedItems: Map<number, LaneEntity[]>;
  protected spawnedObstacles: Map<number, Obstacle[]>;
  protected spawnedBoostPads: Map<number, BoostPadAnimated[]>;
  protected lastObstacleSpawnedPerLane: Map<number, number>;
  protected lastBoostPadSpawnedPerLane: Map<number, number>;
  protected lastSpawnedDropOff: number;
  protected itemTracking: Map<string, ItemTracking>;
  protected previousSpawnEdgeX: number;
  protected endPointX: number;

  constructor(scene: Phaser.Scene, level: Level, theme: Theme) {
    this.scene = scene;
    this.currentLevel = level;
    this.currentTheme = theme;

    this.obstacleFactory = new ObstacleFactory(this.scene);
    this.entityFactory = new EntityFactory(this.scene);

    this.obstacleList = [];
    this.itemConfiguration = new Map();
    this.hasDroppedAllPassengers = false;
    this.hasSpawnedAllDropPoints = false;

    this.passengers = [];
    this.taxiRanks = [];
    this.taxiDropOffs = [];
    this.itemZones = new Map();
    this.spawnedItems = new Map();
    this.spawnedObstacles = new Map();
    this.spawnedBoostPads = new Map();
    this.lastObstacleSpawnedPerLane = new Map();
    this.lastBoostPadSpawnedPerLane = new Map();
    this.lastSpawnedDropOff = 0;
    this.itemTracking = new Map();
    this.previousSpawnEdgeX = 0;
    this.endPointX = 0;
  }

  get CurrentLevel(): Level {
    return this.currentLevel;
  }

  get ItemDetails(): LaneItemDetails {
    return this.currentLevel.laneItemDetails;
  }

  get CurrentTheme(): Theme {
    return this.currentTheme;
  }

  get LevelEndPointX(): number {
    let cameraSafeZone = 500;
    return this.endPointX + cameraSafeZone;
  }

  get FurthestXDropOff(): number {
    return this.lastSpawnedDropOff;
  }

  get HasDroppedAllPassengers(): boolean {
    return this.hasDroppedAllPassengers;
  }

  get HasSpawnedAllDropOffs(): boolean {
    return this.hasSpawnedAllDropPoints;
  }

  public init(trafficManager: TrafficManager, floorLayer: Phaser.GameObjects.Layer, foregroundLayer: Phaser.GameObjects.Layer) {
    this.trafficManager = trafficManager;
    this.floorLayer = floorLayer;
    this.foregroundPavementLayer = foregroundLayer;

    this.obstacleFactory.init();
    this.entityFactory.init();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  public changeLevel(level: Level, theme: Theme) {
    this.currentLevel = level;
    this.currentTheme = theme;
  }

  public setupLevel(startX: number, startY: number, initialDistanceToGenerate: number) {
    this.clearLevel();

    let rankZoneSize = 150;
    // Setup all the levels passengers ahead of time and sort them by distance.
    for (const passengerJourney of this.currentLevel.passengerDetails.passengers) {
      let passenger = new Passenger(this.currentLevel.passengerDetails, passengerJourney);
      this.passengers.push(passenger);

      this.createZone([0, 1, 2, 3], {
        startX: passenger.PickupPoint - rankZoneSize,
        endX: passenger.PickupPoint + rankZoneSize
      });

      this.createZone([0, 1, 2, 3], {
        startX: passenger.DropOffPoint - rankZoneSize,
        endX: passenger.DropOffPoint + rankZoneSize
      });

      if (passenger.DropOffPoint > this.endPointX) {
        this.endPointX = passenger.DropOffPoint;
      }
    }
    this.passengers.sort((passengerA, passengerB): number => passengerA.PickupPoint - passengerB.PickupPoint);

    // console.log('Zones', this.itemZones);

    // Build obstacle configurations.
    for (const obstacleName of this.ItemDetails.allowedObstacles) {
      let config = this.ItemDetails.obstacleConfiguration[obstacleName];
      let obstacleClass: typeof Obstacle = ObstacleFactory.obstacleNameToClass[obstacleName as ObstacleNameValues];

      let itemConfig: ItemConfig = {
        className: obstacleClass.name,
        maxSpawned: config?.maxSpawned ?? this.ItemDetails.defaultMaxSpawned,
        timeBetweenSpawns: config?.timeBetweenSpawns ?? this.ItemDetails.defaultTimeBetweenSpawns
      };

      this.itemConfiguration.set(obstacleClass.name, itemConfig);
      this.obstacleList.push(obstacleClass);
    }

    // Make sure we remember to add a boost config.
    this.itemConfiguration.set(BoostPadAnimated.name, {
      className: BoostPadAnimated.name,
      maxSpawned: this.ItemDetails.boostConfiguration.maxSpawned,
      timeBetweenSpawns: this.ItemDetails.boostConfiguration.timeBetweenSpawns
    });
  }

  public clearLevel() {
    this.obstacleList = [];
    this.itemConfiguration.clear();

    this.passengers = [];
    this.spawnedItems.clear();
    this.spawnedObstacles.clear();
    this.spawnedBoostPads.clear();

    for (let i = 0; i < this.currentTheme.roadDetails.laneCount; i++) {
      this.itemZones.set(i, []);
      this.spawnedItems.set(i, []);
      this.spawnedBoostPads.set(i, []);
      this.lastObstacleSpawnedPerLane.set(i, 0);
      this.lastBoostPadSpawnedPerLane.set(i, 0);
    }
  }

  public findNearestObstacleToPoint(frontPointX: number, backPointX: number): Map<number, LaneItem | undefined> {
    let nextObstacle: Map<number, LaneItem | undefined> = new Map();

    for (const [laneNumber, laneObstacles] of this.spawnedObstacles) {
      let nearestItem = nextObstacle.get(laneNumber);

      if (laneObstacles.length <= 0) {
        // nextObstacle.set(laneNumber, nearestEntity);
        continue;
      }

      for (const obstacle of laneObstacles) {
        let bounds = (obstacle.body as MatterJS.BodyType).bounds;

        // Is in front of vehicle?
        if (bounds.min.x > frontPointX) {
          if (!nearestItem || bounds.min.x < nearestItem.distance) {
            if (!nearestItem) nearestItem = { classification: ObjectClassification.Obstacle, distance: 0 };
            nearestItem.classification = ObjectClassification.Obstacle;
            nearestItem.distance = (obstacle.body as MatterJS.BodyType).bounds.min.x;
          }
          // Is inside of vehicle?
        } else if (MathExtras.getOverlap(backPointX, frontPointX, bounds.min.x, bounds.max.x)) {
          if (!nearestItem) nearestItem = { classification: ObjectClassification.Obstacle, distance: 0 };
          nearestItem.classification = ObjectClassification.Obstacle;
          nearestItem.distance = frontPointX;
        }
      }

      nextObstacle.set(laneNumber, nearestItem);
    }

    return nextObstacle;
  }

  public findNearestBoostPadToPoint(frontPointX: number, backPointX: number) {
    let nextBoostPad: Map<number, LaneItem | undefined> = new Map();

    for (const [laneNumber, laneBoosts] of this.spawnedBoostPads) {
      let nearestX = Number.MAX_SAFE_INTEGER;

      if (laneBoosts.length <= 0) {
        continue;
      }

      for (const boosts of laneBoosts) {
        if (boosts.x > frontPointX && boosts.x < nearestX) {
          nearestX = boosts.x;
        }
      }

      if (nearestX < Number.MAX_SAFE_INTEGER) nextBoostPad.set(laneNumber, { classification: ObjectClassification.BoostPad, distance: nearestX });
    }

    return nextBoostPad;
  }

  public checkOnPassengers(rightViewBoundsX: number) {
    const spawnRange = 4000;

    let numPassengersDropped = 0;
    let allDropPointsSpawned = true;

    for (const passenger of this.passengers) {
      if (passenger.State == PassengerState.ReadyToSpawn) {
        if (passenger.PickupPoint > rightViewBoundsX && passenger.PickupPoint < rightViewBoundsX + spawnRange) {
          // Spawn taxi rank and passenger.
          this.spawnPassengerPickup(passenger);
          passenger.spawned();
        }
      } else if (!passenger.HasWhistled && passenger.PickupPoint < rightViewBoundsX && passenger.State == PassengerState.WaitingForPickup) {
        app.audioHandler.playSound(this.scene, 'SndWhistle', 'sfx');
        passenger.whistle();
      } else if (passenger.State == PassengerState.OnRoute) {
        if (passenger.DropOffPoint > rightViewBoundsX && passenger.DropOffPoint < rightViewBoundsX + spawnRange) {
          // Spawn drop off point.
          this.spawnPassengerDropOff(passenger);
          passenger.nearDropOff();
        }
      } else if (passenger.State == PassengerState.AtDestination) {
        numPassengersDropped++;
      }

      if (passenger.State != PassengerState.AtDestination && passenger.State != PassengerState.ReadyToDropOff) {
        allDropPointsSpawned = false;
      }
    }

    // Check if we've spawned all the drop points.
    if (allDropPointsSpawned) this.hasSpawnedAllDropPoints = true;

    // Check if we've dropped all passengers.
    if (numPassengersDropped == this.passengers.length) this.hasDroppedAllPassengers = true;
  }

  public spawnPassengerPickup(passenger: Passenger) {
    // Spawn passenger.
    let waitingPassenger = new WaitingPassenger(this.scene, passenger.PickupPoint, LevelUtil.getPavementYMid(this.currentTheme.roadDetails));
    waitingPassenger.init(passenger);
    this.foregroundPavementLayer?.add(waitingPassenger);

    // Spawn taxi rank.
    if (this.taxiRanks.length > 0) {
      let rank = this.taxiRanks[0];
      rank.x = passenger.PickupPoint;
      rank.y = LevelUtil.RoadMiddle;
    } else {
      let rank = new TaxiRank(this.scene, passenger.PickupPoint, LevelUtil.RoadMiddle);
      rank.init();
      this.floorLayer?.add(rank);
      rank.setPassenger(waitingPassenger, passenger);
    }
  }

  public spawnPassengerDropOff(passenger: Passenger) {
    // Spawn taxi rank.
    if (this.taxiDropOffs.length > 0) {
      let rank = this.taxiDropOffs[0];
      rank.x = passenger.DropOffPoint;
      rank.y = LevelUtil.RoadMiddle;
    } else {
      let taxiDropPoint = new TaxiDropOff(this.scene, passenger.DropOffPoint, LevelUtil.RoadMiddle);
      taxiDropPoint.init();
      this.floorLayer?.add(taxiDropPoint);
      taxiDropPoint.setPassenger(passenger);
    }

    if (passenger.DropOffPoint > this.lastSpawnedDropOff) this.lastSpawnedDropOff = passenger.DropOffPoint;
  }

  public spawnEntitiesAhead(time: number, delta: number, rightViewBoundsX: number) {
    const safeSpawnDistance: number = 500;

    // Make sure we're not spawning in the same location that we did last time.
    if (rightViewBoundsX < this.previousSpawnEdgeX + this.ItemDetails.antiRepeatSpawnDistance) return;

    this.previousSpawnEdgeX = rightViewBoundsX;

    // Find free lanes.
    // let validLanes: Map<number, boolean> = new Map();
    let validLanes: number[] = [];
    for (const [laneNumber, laneEntities] of this.spawnedItems) {
      if (laneEntities.length <= 0) {
        validLanes.push(laneNumber);
        continue;
      }

      let lastSpawned = laneEntities[laneEntities.length - 1];
      if (lastSpawned.x < rightViewBoundsX + safeSpawnDistance) {
        validLanes.push(laneNumber);
        // validLanes.set(laneNumber, true);
      } else {
        // validLanes.set(laneNumber, false);
      }
    }

    let lanesToFill = Phaser.Math.Between(this.ItemDetails.minLanesAtOnce, this.ItemDetails.maxLanesAtOnce);
    Phaser.Utils.Array.Shuffle(validLanes);

    // Try spawn BoostPads.
    // Try spawn obstacles.
    let loopCount = 0;
    let targetLanesLeft = this.currentTheme.roadDetails.laneCount - lanesToFill;
    while (validLanes.length > targetLanesLeft && loopCount <= 3) {
      this.spawnBoostAhead(rightViewBoundsX, validLanes, safeSpawnDistance);
      if (validLanes.length <= targetLanesLeft) break;
      this.spawnObstaclesAhead(rightViewBoundsX, validLanes, safeSpawnDistance);
      loopCount++;
    }
  }

  public despawnEntitiesBehindOrUsed(time: number, delta: number, leftViewBoundsX: number) {
    const safeDespawnDistance: number = 500;

    for (const [laneNumber, laneEntities] of this.spawnedItems) {
      if (laneEntities.length <= 0) continue;

      for (let i = laneEntities.length - 1; i >= 0; i--) {
        const entity = laneEntities[i];

        if (entity.HasBeenHit || entity.x <= leftViewBoundsX - safeDespawnDistance) {
          laneEntities.splice(i, 1);
          this.untrackItem(entity, laneNumber);
          entity.recycle();
        }
      }
    }
  }

  public spawnBoostAhead(rightViewBoundsX: number, validLanes: number[], safeDespawnDistance: number) {
    let trackedItem = this.itemTracking.get(BoostPadAnimated.name);
    let itemConfig = this.itemConfiguration.get(BoostPadAnimated.name);
    if ((trackedItem?.currentSpawned ?? 0) >= (itemConfig?.maxSpawned ?? this.ItemDetails.defaultMaxSpawned)) return;
    if (this.scene.sys.game.loop.time - (trackedItem?.lastSpawn ?? 0) < (itemConfig?.timeBetweenSpawns ?? this.ItemDetails.defaultTimeBetweenSpawns)) return;

    // Weight BoostPad spawning by last spawned distance.
    let laneWeights: LaneWeight[] = this.buildWeights(validLanes, this.lastBoostPadSpawnedPerLane, rightViewBoundsX, safeDespawnDistance);

    if (laneWeights.length == 1) {
      let entry = laneWeights[0];

      // Spawn BoostPad.
      if (this.spawnBoost(rightViewBoundsX, entry.laneNumber)) {
        const index = validLanes.indexOf(entry.laneNumber);
        if (index > -1) validLanes.splice(index, 1);
      }
    } else if (laneWeights.length > 1) {
      const maxValue = laneWeights[laneWeights.length - 1].weight;
      const randomNum = maxValue * Math.random();

      for (const entry of laneWeights) {
        if (entry.weight >= randomNum) {
          // Spawn BoostPad.
          if (this.spawnBoost(rightViewBoundsX, entry.laneNumber)) {
            const index = validLanes.indexOf(entry.laneNumber);
            if (index > -1) validLanes.splice(index, 1);
          }
          break;
        }
      }
    }
  }

  public spawnObstaclesAhead(rightViewBoundsX: number, validLanes: number[], safeDespawnDistance: number) {
    let laneWeights: LaneWeight[] = this.buildWeights(validLanes, this.lastObstacleSpawnedPerLane, rightViewBoundsX, safeDespawnDistance);

    if (laneWeights.length == 1) {
      let entry = laneWeights[0];

      if (this.spawnObstacle(rightViewBoundsX, entry.laneNumber)) {
        const index = validLanes.indexOf(entry.laneNumber);
        if (index > -1) validLanes.splice(index, 1);
      }
    } else if (laneWeights.length > 1) {
      const maxValue = laneWeights[laneWeights.length - 1].weight;
      const randomNum = maxValue * Math.random();

      for (const entry of laneWeights) {
        if (entry.weight >= randomNum) {
          if (this.spawnObstacle(rightViewBoundsX, entry.laneNumber)) {
            const index = validLanes.indexOf(entry.laneNumber);
            if (index > -1) validLanes.splice(index, 1);
          }
          break;
        }
      }
    }
  }

  public spawnBoost(startX: number, laneNumber: number): boolean {
    let distance = startX + Phaser.Math.Between(this.ItemDetails.minSpawnAheadDistance, this.ItemDetails.maxSpawnAheadDistance);

    let pad = this.entityFactory.makeEntity<BoostPadAnimated>(BoostPadAnimated, distance, LevelUtil.getLaneYFromTopToMid(laneNumber, this.currentTheme.roadDetails));

    // Check to make sure we're not spawning on a taxi rank or past the level end.
    if (!this.checkLanePointFree(laneNumber, (pad?.body as MatterJS.BodyType).bounds.min.x, (pad?.body as MatterJS.BodyType).bounds.max.x)) {
      // console.log(`Blocked spawn at ${(pad?.body as MatterJS.BodyType).bounds.min.x} to ${(pad?.body as MatterJS.BodyType).bounds.max.x}`);
      pad?.recycle();
      return false;
    }

    if (pad) {
      pad.boostSpeed = 10;
      pad.setScale(LevelUtil.getLaneYVehicleScale(laneNumber, this.currentTheme.roadDetails));
      pad.setDepth(laneNumber);

      this.floorLayer?.add(pad as unknown as Phaser.GameObjects.GameObject);
      this.trackItem(pad, laneNumber, this.lastBoostPadSpawnedPerLane);

      return true;
    } else {
      return false;
    }
  }

  public spawnObstacle(startX: number, laneNumber: number): boolean {
    let distance = startX + Phaser.Math.Between(this.ItemDetails.minSpawnAheadDistance, this.ItemDetails.maxSpawnAheadDistance);
    let randomIndex = Phaser.Math.Between(0, this.obstacleList.length == 0 ? 0 : this.obstacleList.length - 1);
    let obstacleToSpawn = this.obstacleList[randomIndex];

    let trackedItem = this.itemTracking.get(obstacleToSpawn.name);
    let itemConfig = this.itemConfiguration.get(obstacleToSpawn.name);
    if ((trackedItem?.currentSpawned ?? 0) >= (itemConfig?.maxSpawned ?? this.ItemDetails.defaultMaxSpawned)) return false;
    if (this.scene.sys.game.loop.time - (trackedItem?.lastSpawn ?? 0) < (itemConfig?.timeBetweenSpawns ?? this.ItemDetails.defaultTimeBetweenSpawns))
      return false;

    let obstacle = this.obstacleFactory.makeObstacle<Obstacle>(
      obstacleToSpawn,
      distance,
      LevelUtil.getLaneYFromTopToMid(laneNumber, this.currentTheme.roadDetails)
      // this.untrackItem
    );

    // Check to make sure we're not spawning on a taxi rank or past the level end.
    if (!this.checkLanePointFree(laneNumber, (obstacle?.body as MatterJS.BodyType).bounds.min.x, (obstacle?.body as MatterJS.BodyType).bounds.max.x)) {
      // console.log(`Blocked spawn at ${(obstacle?.body as MatterJS.BodyType).bounds.min.x} to ${(obstacle?.body as MatterJS.BodyType).bounds.max.x}`);
      obstacle?.recycle();
      return false;
    }

    if (obstacle) {
      obstacle.setScale(LevelUtil.getLaneYVehicleScale(laneNumber, this.currentTheme.roadDetails));
      obstacle.setDepth(laneNumber);

      this.floorLayer?.add(obstacle);
      this.trackItem(obstacle, laneNumber, this.lastObstacleSpawnedPerLane);

      return true;
    } else {
      return false;
    }
  }

  public createZone(lanes: number[], zone: LaneZone) {
    for (const lane of lanes) {
      let zones = this.itemZones.get(lane);
      if (!Array.isArray(zones)) zones = [];
      zones.push(zone);

      zones.sort((a, b) => a.startX - b.startX);
      this.itemZones.set(lane, zones);
    }
  }

  public checkLanePointFree(lane: number, startPoint: number, endPoint: number) {
    let zones = this.itemZones.get(lane);

    if (endPoint > this.endPointX) return false;

    for (const [index, zone] of zones?.entries() ?? []) {
      if (MathExtras.getOverlap(zone.startX, zone.endX, startPoint, endPoint)) {
        return false;
      }
    }

    return true;
  }

  // Tear down.
  public endLevel() {}

  protected buildWeights(validLanes: number[], lastLaneSpawn: Map<number, number>, rightViewBoundsX: number, safeDespawnDistance: number): LaneWeight[] {
    // Weight spawning by last spawned distance.
    let laneWeights: LaneWeight[] = [];

    for (const [index, laneNumber] of validLanes.entries()) {
      let distance = lastLaneSpawn.get(laneNumber);
      if (typeof distance == 'number' && distance + safeDespawnDistance < rightViewBoundsX) {
        laneWeights.push({
          laneNumber: laneNumber,
          distance: distance,
          weight: rightViewBoundsX - distance + (laneWeights.length == 0 ? 0 : laneWeights[laneWeights.length - 1].weight)
        });
      }
    }

    return laneWeights;
  }

  protected trackItem(item: LaneEntity, laneNumber: number, perLaneTypeTracker: Map<number, number>) {
    let laneItems: LaneEntity[] | undefined = this.spawnedItems.get(laneNumber);
    if (!laneItems) laneItems = [];
    laneItems.push(item);
    this.spawnedItems.set(laneNumber, laneItems);

    if (item instanceof Obstacle) {
      let laneObstacles = this.spawnedObstacles.get(laneNumber);
      if (!laneObstacles) laneObstacles = [];
      laneObstacles.push(item);
      this.spawnedObstacles.set(laneNumber, laneObstacles);
    }

    if (item instanceof BoostPadAnimated) {
      let laneBoosts = this.spawnedBoostPads.get(laneNumber);
      if (!laneBoosts) laneBoosts = [];
      laneBoosts.push(item as BoostPadAnimated);
      this.spawnedBoostPads.set(laneNumber, laneBoosts);
    }

    perLaneTypeTracker.set(laneNumber, item.x);

    let trackedItem = this.itemTracking.get(item.constructor.name);
    if (!trackedItem) trackedItem = { className: item.constructor.name, currentSpawned: 0, lastSpawn: 0 };
    trackedItem.currentSpawned++;
    trackedItem.lastSpawn = this.scene.sys.game.loop.time;
    this.itemTracking.set(item.constructor.name, trackedItem);
  }

  protected untrackItem(item: LaneEntity, laneNumber: number) {
    if (item instanceof Obstacle) {
      let laneObstacles = this.spawnedObstacles.get(laneNumber);
      if (laneObstacles) {
        let index = laneObstacles.indexOf(item);
        if (index > -1) laneObstacles.splice(index, 1);
        this.spawnedObstacles.set(laneNumber, laneObstacles);
      }
    }

    if (item instanceof BoostPadAnimated) {
      let laneBoosts = this.spawnedBoostPads.get(laneNumber);
      if (laneBoosts) {
        let index = laneBoosts.indexOf(item);
        if (index > -1) laneBoosts.splice(index, 1);
        this.spawnedBoostPads.set(laneNumber, laneBoosts);
      }
    }

    let trackedItem = this.itemTracking.get(item.constructor.name);
    if (!trackedItem) trackedItem = { className: item.constructor.name, currentSpawned: 0, lastSpawn: 0 };
    else trackedItem.currentSpawned--;
    this.itemTracking.set(item.constructor.name, trackedItem);
  }

  protected shutdown() {
    this.passengers = [];

    this.spawnedItems.clear();
    this.spawnedObstacles.clear();
    this.spawnedBoostPads.clear();

    this.itemTracking.clear();
  }
}
